"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.decodeCacheSourceLibraries = decodeCacheSourceLibraries;
const OrcadDsnCfbCachePrimitiveFrames_1 = require("./OrcadDsnCfbCachePrimitiveFrames");
const OrcadDsnCfbRecordDecoders_1 = require("./OrcadDsnCfbRecordDecoders");
const OrcadDsnCfbStreamUtils_1 = require("./OrcadDsnCfbStreamUtils");
const OrcadDsnCfbTypedObjectFrames_1 = require("./OrcadDsnCfbTypedObjectFrames");
var CacheCatalogueRecordKind;
(function (CacheCatalogueRecordKind) {
    CacheCatalogueRecordKind["PhysicalHeader"] = "physical_header";
})(CacheCatalogueRecordKind || (CacheCatalogueRecordKind = {}));
function decodeCacheSourceLibraries(bytes, headers, diagnostics, primitives = OrcadDsnCfbRecordDecoders_1.OrcadDsnCfbRecordDecoders.decodeOrCadDsnCfbPrimitiveRecords(bytes).records) {
    const sources = [];
    const pinFrames = (0, OrcadDsnCfbTypedObjectFrames_1.readTypedObjectFrames)(bytes, [0x1a, 0x1b], 1, diagnostics, 'Root Entry/Cache');
    const headersByOffset = new Map(headers.map((header) => [header.offset, header]));
    const primitivesByOffset = new Map(primitives.map((primitive) => [primitive.offset, primitive]));
    const records = new Map();
    const physicalHeaders = new Map();
    OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.forEachTerminatedPayload(bytes, (record) => records.set(record.offset, record));
    for (const record of records.values()) {
        const header = headersByOffset.get(record.offset);
        const end = Math.min(record.offset + record.payloadByteLength, header ? header.offset + header.payloadByteLength : bytes.length);
        const name = readCountedLibraryField(bytes, record.offset, end);
        const library = name ? readCountedLibraryField(bytes, OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.lengthPrefixedStringEnd(name), end) : undefined;
        const markerOffset = library ? OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.lengthPrefixedStringEnd(library) : undefined;
        const viewMarker = markerOffset !== undefined &&
            markerOffset + 6 <= end &&
            OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readUInt32LE(bytes, markerOffset) === 0x30;
        const terminated = record.byteLength > record.payloadByteLength &&
            end === record.offset + record.payloadByteLength &&
            (!header || record.offset + record.byteLength <= header.offset + header.byteLength);
        const primitiveExtent = {};
        const decodedPrimitiveCount = viewMarker && markerOffset !== undefined ?
            (0, OrcadDsnCfbCachePrimitiveFrames_1.countCachePrimitiveFrames)(bytes, markerOffset + 6, record, records, primitivesByOffset, diagnostics, primitiveExtent)
            : undefined;
        const declaredPrimitiveCount = viewMarker && markerOffset !== undefined ?
            OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readUInt16LE(bytes, markerOffset + 4)
            : undefined;
        const view = viewMarker && terminated && declaredPrimitiveCount === decodedPrimitiveCount;
        if (view && header?.kind === 'view-header' && primitiveExtent.endOffset !== undefined) {
            const tableOffset = primitiveExtent.endOffset;
            const table = records.get(tableOffset);
            if (table &&
                table.payloadByteLength >= 22 &&
                table.byteLength > table.payloadByteLength &&
                OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readUInt32LE(bytes, tableOffset) === 0 &&
                pinFrames.get(tableOffset + table.byteLength)?.offset === tableOffset + 10) {
                const fields = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
                const width = fields.getInt16(tableOffset + 4, true);
                const height = fields.getInt16(tableOffset + 6, true);
                if (width < 0 || height < 0) {
                    delete header.declaredBoundingBox;
                    diagnostics.push({
                        severity: 'warning',
                        code: 'cache-view-bounds-withheld',
                        offset: tableOffset,
                        message: `CFB view bounds: unsupported signed extent width=${width} height=${height}; declared box withheld`,
                    });
                }
                else {
                    header.declaredBoundingBox = { X1: 0, Y1: 0, X2: width, Y2: height };
                }
            }
        }
        const catalogue = markerOffset !== undefined && terminated ? readViewCatalogueBody(bytes, markerOffset, end) : undefined;
        if (catalogue?.physicalHeader) {
            const bodyOffset = record.offset + record.byteLength;
            physicalHeaders.set(bodyOffset, {
                kind: CacheCatalogueRecordKind.PhysicalHeader,
                bodyOffset,
                ...catalogue.physicalHeader,
            });
        }
        const physicalHeader = physicalHeaders.get(record.offset);
        const physical = (markerOffset !== undefined &&
            terminated &&
            physicalHeader?.kind === CacheCatalogueRecordKind.PhysicalHeader &&
            physicalHeader.name.value === name?.value &&
            physicalHeader.library.value === library?.value) ?
            readPhysicalCatalogueBody(bytes, markerOffset, end, record.offset + record.byteLength, records, physicalHeader)
            : undefined;
        const physicalCountAgrees = physical !== undefined && physical.declaredSectionCount === physical.decodedSectionCount;
        if (name && library && (view || catalogue || physicalCountAgrees)) {
            sources.push({
                headerOffset: record.offset,
                library,
                ...(physicalCountAgrees ? { physicalPackageName: name.value, physicalSections: physical.sections } : {}),
                ...(catalogue?.continuationLibrary ? { continuationLibrary: catalogue.continuationLibrary } : {}),
            });
            if (physicalCountAgrees && physical?.nextHeader) {
                physicalHeaders.set(physical.nextHeader.bodyOffset, physical.nextHeader);
            }
        }
        else if (header?.kind === 'view-header' || library?.value) {
            const code = viewMarker && !terminated ? 'cache-source-library-unterminated'
                : viewMarker && declaredPrimitiveCount !== decodedPrimitiveCount ?
                    'cache-source-library-primitive-count-mismatch'
                    : physical && !physicalCountAgrees ? 'cache-source-library-section-count-mismatch'
                        : (physicalHeader &&
                            (physicalHeader.name.value !== name?.value || physicalHeader.library.value !== library?.value)) ?
                            'cache-source-library-physical-header-mismatch'
                            : 'cache-source-library-unframed';
            diagnostics.push({
                severity: 'warning',
                code,
                message: `CFB cache source library [${code}] header=${JSON.stringify(name?.value)} offset=${record.offset}${viewMarker ? ` declared=${declaredPrimitiveCount} decoded=${decodedPrimitiveCount}`
                    : physical ?
                        ` declared=${physical.declaredSectionCount} decoded=${physical.decodedSectionCount ?? 'unframed'}`
                        : ''}`,
                streamPath: 'Root Entry/Cache',
                offset: record.offset,
            });
        }
    }
    return sources;
}
function readPhysicalCatalogueBody(bytes, offset, end, sectionOffset, records, header) {
    const reference = readCountedLibraryField(bytes, offset, end);
    const footprint = reference ?
        readCountedLibraryField(bytes, OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.lengthPrefixedStringEnd(reference) + 3, end)
        : undefined;
    if (!footprint) {
        return undefined;
    }
    const countOffset = OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.lengthPrefixedStringEnd(footprint);
    if (countOffset + 2 > end || !readTaggedTail(bytes, countOffset + 2, end, 0x20, 0xffff)) {
        return undefined;
    }
    const sections = readPhysicalSectionCount(bytes, sectionOffset, records, header.name);
    return {
        declaredSectionCount: OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readUInt16LE(bytes, countOffset),
        decodedSectionCount: sections?.count,
        sections: sections?.sections,
        ...(sections?.nextHeader ? { nextHeader: sections.nextHeader } : {}),
    };
}
function readPhysicalSectionCount(bytes, offset, records, name) {
    let cursor = offset;
    let count = 0;
    const sections = [];
    while (cursor < bytes.length) {
        const record = records.get(cursor);
        if (!record) {
            return undefined;
        }
        const end = record.offset + record.payloadByteLength;
        const selector = readCountedLibraryField(bytes, cursor, end);
        const cell = selector ?
            readCountedLibraryField(bytes, OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.lengthPrefixedStringEnd(selector), end)
            : undefined;
        if (!cell?.value) {
            return undefined;
        }
        cursor = OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.lengthPrefixedStringEnd(cell);
        if (cursor + 2 > end) {
            return undefined;
        }
        const pinCount = OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readUInt16LE(bytes, cursor);
        cursor += 2;
        const pinNumbers = [];
        for (let pinIndex = 0; pinIndex < pinCount; pinIndex++) {
            const pin = readCountedLibraryField(bytes, cursor, end);
            if (!pin) {
                return undefined;
            }
            pinNumbers.push(pin.value);
            cursor = OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.lengthPrefixedStringEnd(pin) + 1;
            if (cursor > end) {
                return undefined;
            }
        }
        count++;
        sections.push({ offset: record.offset, selector: selector?.value ?? '', cellName: cell.value, pinNumbers });
        if (end === bytes.length) {
            return readEmptyCatalogueEntries(bytes, cursor, end) ? { count, sections } : undefined;
        }
        if (record.byteLength === record.payloadByteLength) {
            return undefined;
        }
        if (readTaggedTail(bytes, cursor, end, 0x20, 0xffff)) {
            cursor = record.offset + record.byteLength;
            continue;
        }
        const library = readCatalogueLibraryPreamble(bytes, cursor, end, 0x1f, 0);
        const named = readCataloguePreamble(bytes, cursor, end, false, 0x1f, 0);
        if (Boolean(library) === Boolean(named)) {
            return undefined;
        }
        const reference = named ?? (library ? { name, library } : undefined);
        return reference ?
            {
                count,
                sections,
                nextHeader: {
                    kind: CacheCatalogueRecordKind.PhysicalHeader,
                    bodyOffset: record.offset + record.byteLength,
                    ...reference,
                },
            }
            : undefined;
    }
    return undefined;
}
function readEmptyCatalogueEntries(bytes, offset, end) {
    let cursor = offset;
    while (cursor < end) {
        const name = readCountedLibraryField(bytes, cursor, end);
        if (!name?.value) {
            return false;
        }
        cursor = OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.lengthPrefixedStringEnd(name);
        if (cursor + 2 > end || OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readUInt16LE(bytes, cursor) !== 0) {
            return false;
        }
        cursor += 2;
    }
    return cursor === end;
}
function readViewCatalogueBody(bytes, offset, end) {
    if (offset + 2 > end) {
        return undefined;
    }
    const count = OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readUInt16LE(bytes, offset);
    let cursor = offset + 2;
    for (let index = 0; index < count; index++) {
        const view = readCountedLibraryField(bytes, cursor, end);
        if (!view) {
            return undefined;
        }
        cursor = OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.lengthPrefixedStringEnd(view);
    }
    const continuation = readCataloguePreamble(bytes, cursor, end, false);
    const libraryContinuation = readCatalogueLibraryPreamble(bytes, cursor, end, 0x06, 0xffff);
    let tableCursor = cursor;
    while (tableCursor < end) {
        const name = readCountedLibraryField(bytes, tableCursor, end);
        const countOffset = name ? OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.lengthPrefixedStringEnd(name) : end;
        if (countOffset + 2 > end || OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readUInt16LE(bytes, countOffset) !== 0) {
            break;
        }
        tableCursor = countOffset + 2;
    }
    const tableBoundary = readCataloguePreamble(bytes, tableCursor, end, true);
    if ([continuation, tableBoundary, libraryContinuation].filter(Boolean).length !== 1) {
        return undefined;
    }
    return {
        ...(libraryContinuation ? { continuationLibrary: libraryContinuation } : {}),
        ...(tableBoundary ? { physicalHeader: tableBoundary } : {}),
    };
}
function readCataloguePreamble(bytes, offset, end, tableBoundary, tag = tableBoundary ? 0x1f : 0x06, closingWord = tableBoundary ? 0 : 0xffff) {
    let cursor = offset + (tableBoundary ? 2 : 0);
    while (cursor < end) {
        const name = readCountedLibraryField(bytes, cursor, end);
        if (!name) {
            return undefined;
        }
        cursor = OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.lengthPrefixedStringEnd(name);
        if (cursor + 2 > end) {
            return undefined;
        }
        const count = OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readUInt16LE(bytes, cursor);
        cursor += 2;
        if (count > 0) {
            const library = readCatalogueLibraryPreamble(bytes, cursor, end, tag, closingWord);
            return library ? { name, library } : undefined;
        }
    }
    return undefined;
}
function readCatalogueLibraryPreamble(bytes, offset, end, tag, closingWord) {
    const library = readCountedLibraryField(bytes, offset, end);
    if (!library) {
        return undefined;
    }
    const cursor = OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.lengthPrefixedStringEnd(library) + 8;
    return (cursor + 2 <= end &&
        OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readUInt16LE(bytes, cursor) === tag &&
        readTaggedTail(bytes, cursor + 2, end, tag, closingWord)) ?
        library
        : undefined;
}
function readTaggedTail(bytes, offset, end, tag, closingWord) {
    let cursor = offset;
    while (cursor + 3 < end) {
        if (cursor + 9 > end || bytes[cursor] !== tag) {
            return false;
        }
        cursor += 9;
    }
    return (cursor + 3 === end &&
        bytes[cursor] === tag &&
        OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readUInt16LE(bytes, cursor + 1) === closingWord);
}
function readCountedLibraryField(bytes, offset, end) {
    if (offset < 0 || offset + 2 > end || end > bytes.length) {
        return undefined;
    }
    const length = OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readUInt16LE(bytes, offset);
    const terminator = offset + 2 + length;
    if (terminator >= end || bytes[terminator] !== 0) {
        return undefined;
    }
    const valueBytes = bytes.subarray(offset + 2, terminator);
    if (valueBytes.some((byte) => byte < 0x20)) {
        return undefined;
    }
    return {
        offset,
        length,
        hasNullTerminator: true,
        value: Array.from(valueBytes, (byte) => String.fromCharCode(byte)).join(''),
    };
}
//# sourceMappingURL=OrcadDsnCfbCacheSourceLibraries.js.map